import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { DataTable, ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { useCan } from '../../auth/AuthProvider';
import { teamApi, teamKeys, type Branch } from '../api';
import { BranchDialog } from '../components/BranchDialog';

export function BranchesPage() {
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Branch | undefined>();
  const [open, setOpen] = useState(false);
  const branches = useQuery({ queryKey: teamKeys.branches, queryFn: () => teamApi.branches() });
  const toggle = useMutation({
    mutationFn: (b: Branch) =>
      teamApi.setBranchStatus(b.id, b.status === 'active' ? 'deactivate' : 'activate'),
    onSuccess: async (b) => {
      await queryClient.invalidateQueries({ queryKey: teamKeys.branches });
      toast.success(`${b.name} ${b.status === 'active' ? 'activated' : 'deactivated'}`);
    },
    onError: (err) => toast.error(err),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Branches"
        description="Locations or business units. Staff and leads belong to a branch."
        actions={
          can('branches.create') ? (
            <Button
              leadingIcon={<Plus aria-hidden className="size-4" />}
              onClick={() => {
                setEditing(undefined);
                setOpen(true);
              }}
            >
              New branch
            </Button>
          ) : null
        }
      />
      <Card>
        {branches.isPending ? (
          <TableSkeleton />
        ) : branches.isError ? (
          <div className="p-4">
            <ErrorState error={branches.error} onRetry={() => void branches.refetch()} />
          </div>
        ) : (
          <DataTable
            rows={branches.data.data}
            rowKey={(b) => b.id}
            empty={
              <EmptyState
                icon={<Building2 className="size-6" />}
                title="No branches yet"
                description="Create your first branch to start adding staff."
              />
            }
            columns={[
              { header: 'Name', cell: (b) => <span className="font-medium text-fg">{b.name}</span> },
              { header: 'Code', cell: (b) => <code className="text-xs">{b.code}</code> },
              { header: 'City', cell: (b) => b.city ?? '—' },
              {
                header: 'Status',
                cell: (b) => (
                  <Badge tone={b.status === 'active' ? 'success' : 'neutral'}>
                    {b.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
              {
                header: 'Actions',
                className: 'text-right',
                cell: (b) =>
                  can('branches.edit') ? (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditing(b);
                          setOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggle.mutate(b)}
                        loading={toggle.isPending && toggle.variables?.id === b.id}
                      >
                        {b.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>
      <BranchDialog open={open} onOpenChange={setOpen} {...(editing ? { branch: editing } : {})} />
    </PageContainer>
  );
}
